import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Building2,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  FileSignature,
  Link2,
  Loader2,
  Mail,
  PenLine,
  ScrollText,
  Tag,
  Truck,
  UserPlus,
} from "lucide-react";

type Item = {
  key: string;
  at: string;
  icon: typeof Tag;
  label: string;
  detail?: string | null;
  href?: string | null;
  linkLabel?: string;
};

const STAGE_LABEL: Record<string, string> = {
  pending: "Not started",
  ready: "Ready",
  ein_acquired: "EIN in hand",
  filings_submitted: "Filed with the state",
  filings_received: "State has processed",
  docs_sent: "Documents shipped",
  docs_delivered: "Documents delivered",
};

const LEGAL_LABEL: Record<string, string> = {
  terms: "Terms of use",
  privacy: "Privacy notice",
};

function when(value: string) {
  const d = new Date(value);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function MemberJourneyCard({
  record,
  names,
  events,
}: {
  record: any;
  names: any[];
  events: any[];
}) {
  const [suite, setSuite] = useState<any>(null);
  const [acceptances, setAcceptances] = useState<any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [{ data: suiteRow }, { data: legal }] = await Promise.all([
      (supabase as any)
        .from("suite_numbers")
        .select("number, reserved_at, status")
        .eq("member_record_id", record.id)
        .maybeSingle(),
      record.user_id
        ? (supabase as any)
            .from("legal_acceptances")
            .select("document_key, version, accepted_at")
            .eq("user_id", record.user_id)
        : Promise.resolve({ data: [] }),
    ]);
    setSuite(suiteRow ?? null);
    setAcceptances(legal ?? []);
  }, [record.id, record.user_id]);

  useEffect(() => {
    void load();
  }, [load]);

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];

    if (record.created_at) {
      out.push({
        key: "registered",
        at: record.created_at,
        icon: UserPlus,
        label: "Registered",
        detail: record.email,
      });
    }

    for (const n of names) {
      if (n.submitted_at) {
        out.push({
          key: `name-sub-${n.id}`,
          at: n.submitted_at,
          icon: Tag,
          label: n.event_type === "reopened" ? "Name reopened by the team" : "Name submitted",
          detail: n.submitted_name,
        });
      }
      if (n.decided_at && n.decision) {
        out.push({
          key: `name-dec-${n.id}`,
          at: n.decided_at,
          icon: n.decision === "approved" ? CheckCircle2 : PenLine,
          label: n.decision === "approved" ? "Name approved" : "Change requested",
          detail: [n.submitted_name, n.note].filter(Boolean).join(" — ").replace(" — ", ": "),
        });
      }
    }

    if (record.una_name_confirmed_at) {
      out.push({
        key: "confirmed",
        at: record.una_name_confirmed_at,
        icon: CheckCircle2,
        label: "Record confirmed",
        detail: record.una_name,
      });
    }

    if (record.mea_signed_at) {
      out.push({
        key: "mea",
        at: record.mea_signed_at,
        icon: FileSignature,
        label: "MEA signed",
        href: record.mea_document_url,
        linkLabel: "Open the PDF",
      });
    }

    if (suite?.reserved_at) {
      out.push({
        key: "suite",
        at: suite.reserved_at,
        icon: Building2,
        label: "Suite reserved",
        detail: `Suite ${suite.number}`,
      });
    }

    if (record.lease_signed_at) {
      out.push({
        key: "lease",
        at: record.lease_signed_at,
        icon: FileSignature,
        label: "Lease signed",
        href: record.lease_document_url,
        linkLabel: "Open the PDF",
      });
    }

    if (record.payment_status === "complete") {
      const firstReady = [...events]
        .filter((e) => e.to_stage === "ready")
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())[0];
      out.push({
        key: "payment",
        at: firstReady?.created_at ?? record.updated_at ?? record.created_at,
        icon: CreditCard,
        label: "Payment received",
        detail: "Monthly address fee",
      });
    }

    for (const e of events) {
      out.push({
        key: `stage-${e.id}`,
        at: e.created_at,
        icon: Truck,
        label: `Moved to ${STAGE_LABEL[e.to_stage] ?? e.to_stage}`,
        detail: e.note,
      });
    }

    for (const a of acceptances) {
      out.push({
        key: `legal-${a.document_key}-${a.version}`,
        at: a.accepted_at,
        icon: ScrollText,
        label: `Accepted the ${LEGAL_LABEL[a.document_key] ?? a.document_key}`,
        detail: a.version ? `Version ${a.version}` : null,
      });
    }

    return out
      .filter((i) => Boolean(i.at))
      .sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  }, [record, names, events, suite, acceptances]);

  const copyRegistrationLink = async () => {
    setBusy("link");
    const { data, error } = await supabase.functions.invoke("admin-member-actions", {
      body: { member_record_id: record.id, action: "registration_link" },
    });
    setBusy(null);
    const failure = error?.message || (data as any)?.error;
    const url = (data as any)?.url;
    if (!url) {
      toast.error(String(failure || "We could not find a registration link."));
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Registration link copied");
    } catch (_e) {
      toast.message(url);
    }
  };

  const resendApproval = async () => {
    setBusy("approval");
    const { data, error } = await supabase.functions.invoke("admin-member-actions", {
      body: { member_record_id: record.id, action: "resend_approval" },
    });
    setBusy(null);
    const failure = error?.message || (data as any)?.error;
    if (failure) {
      toast.error(String(failure));
      return;
    }
    toast.success((data as any)?.parked ? "Email parked for sending" : "Approval email sent again");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Journey</CardTitle>
        <CardDescription>Their whole story, oldest first.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {!record.user_id && (
            <Button variant="outline" size="sm" onClick={copyRegistrationLink} disabled={busy === "link"}>
              {busy === "link" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="mr-2 h-4 w-4" />
              )}
              Copy registration link
            </Button>
          )}
          {record.una_name_status === "approved" && (
            <Button variant="outline" size="sm" onClick={resendApproval} disabled={busy === "approval"}>
              {busy === "approval" ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Mail className="mr-2 h-4 w-4" />
              )}
              Resend approval email
            </Button>
          )}
          <Button variant="outline" size="sm" asChild>
            <a href={`/tracking?member=${record.id}`} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              View their tracking page
            </a>
          </Button>
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing has happened yet.</p>
        ) : (
          <ol className="space-y-2">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.key} className="flex items-start gap-3 text-sm">
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-medium">{item.label}</span>
                      <span className="text-xs text-muted-foreground">{when(item.at)}</span>
                    </div>
                    {item.detail ? (
                      <div className="text-muted-foreground break-words">{item.detail}</div>
                    ) : null}
                    {item.href ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs underline underline-offset-2"
                      >
                        {item.linkLabel ?? "Open"}
                      </a>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
